import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import API_CONSTRUCTOR from "../../models/api-model";
import PropertyAPIs from "../../models/property";
import { MapPin, Home, Loader } from "lucide-react";
import { debounce } from "lodash";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
const apiModel = API_CONSTRUCTOR();
const propertyApi = new PropertyAPIs(apiModel);

export const AutoCompleteAddress = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const containerRef = useRef(null);

  // Memoize the debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setQuery(value);
      }, 500),
    []
  );

  const handleInputChange = useCallback(
    (value) => {
      setSelectedLabel(value);
      // Debounce the actual search query
      debouncedSearch(value);
      // Open dropdown when user types
      if (value.length > 0) {
        setOpen(true);
      }
    },
    [debouncedSearch]
  );

  const handleSelect = useCallback(
    (option) => {
      setSelectedLabel(option.label);
      onChange(option);
      setOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length > 0) {
        setLoading(true);
        try {
          const result = await propertyApi.getSuggestions(query);
          const suggestions = result.suggestions || [];
          setOptions(suggestions);
        } catch (error) {
          console.error("Error fetching address suggestions:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setOptions([]);
      }
    };

    fetchSuggestions();
  }, [query]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="w-full relative">
      <Command className="rounded-lg border shadow-sm">
        <CommandInput
          placeholder="Search by address, city, or ZIP code..."
          onValueChange={handleInputChange}
          value={selectedLabel}
          onFocus={() => setOpen(true)}
          className="h-11"
        />
      </Command>

      {open && (options.length > 0 || loading) && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50">
          <Command className="rounded-lg border shadow-lg bg-white">
            <CommandList className="max-h-[300px]">
              <CommandEmpty>
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader className="h-4 w-4 animate-spin" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading...
                    </span>
                  </div>
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No properties found. Try a different search.
                  </div>
                )}
              </CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const { propertyId = "", id, label } = option;
                  return (
                    <CommandItem
                      key={id}
                      value={label}
                      onSelect={() => handleSelect(option)}
                      className="flex items-center gap-3 cursor-pointer py-3 px-4 hover:bg-accent"
                    >
                      <div
                        className={`flex items-center justify-center w-8 h-8 rounded-full ${
                          propertyId
                            ? "bg-blue-100 text-blue-600"
                            : "bg-green-100 text-green-600"
                        }`}
                      >
                        {propertyId ? (
                          <Home className="h-4 w-4" />
                        ) : (
                          <MapPin className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium">{label}</span>
                        {propertyId && (
                          <span className="text-xs text-muted-foreground block">
                            Property ID: {propertyId}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
};

export default AutoCompleteAddress;
